import { useCallback, useState, useEffect } from "react";
import toastError from "../../errors/toastError";

import api from "../../services/api";
import { pedidoEmVoo } from "../../services/pedidosEmVoo";

// Pedidos idênticos EM VOO são um só. O sino do topo e a barra de baixo
// pedem exatamente a mesma lista ("open,pending" com não lidas) no mesmo
// instante da abertura — eram duas requisições iguais. A chave é o conjunto
// de parâmetros (o estoque morre na troca de sessão — ver pedidosEmVoo), e
// cada consumidor recebe uma CÓPIA dos dados, para a mutação de uma lista
// não vazar na outra.
const buscarTickets = (params) =>
  pedidoEmVoo(`tickets:${JSON.stringify(params)}`, () =>
    api.get("/tickets", { params })
  ).then(({ data }) => structuredClone(data));

const useTickets = ({
  searchParam,
  // Lista nova: nome/telefone só no recorte da aba; conteúdo das mensagens na
  // aba ou em todas ("aba" | "todas"). Aditivos ao searchParam.
  filtroContato,
  buscaMensagem,
  escopoBuscaMensagem,
  pageNumber,
  status,
  date,
  showAll,
  queueIds,
  semFila,
  withUnreadMessages,
  isGroup,
  tagIds,
  userIds,
  whatsappIds,
  visao,
  onlyFavorites,
  soAdiados,
  ultimosDias,
  closeReasons,
  // "az" pede a lista pelo nome do contato. Vai ao servidor porque a lista é
  // paginada: ordenar só o que já chegou dá uma ordem que muda a cada rolagem.
  ordem,
  // "false" pede pro backend pular o COUNT (dobra o custo da query) quando a
  // tela só usa hasMore para saber se tem mais página, não um total exibido.
  needsCount = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  // Só na busca: quantos resultados em cada situação (vem na página 1).
  const [contagemPorSituacao, setContagemPorSituacao] = useState(null);
  // Falha de rede virava lista vazia, e a tela dizia "Nenhum cliente
  // esperando" — o pior estado possível, porque mente com cara de normal.
  const [erro, setErro] = useState(false);
  // Uma tentativa nova sem mexer em nenhum filtro.
  const [tentativa, setTentativa] = useState(0);
  const tentarDeNovo = useCallback(() => {
    setErro(false);
    setTentativa((n) => n + 1);
  }, []);

  useEffect(() => {
    // O cancelamento do temporizador só alcança a busca que ainda não saiu. A
    // que já está no ar continua, e sem esta trava a resposta de um recorte
    // abandonado ainda escrevia na tela: marcava erro por cima de um resultado
    // bom, ou repunha a lista de outra aba.
    let atual = true;

    setLoading(true);
    // O erro morre quando uma busca nova começa, e não só quando ela termina:
    // uma resposta 200 legitimamente VAZIA deixava a aba presa no cartão de
    // erro, porque nada além do sucesso com conteúdo limpava a marca.
    setErro(false);
    const delayDebounceFn = setTimeout(() => {
      const fetchTickets = async () => {
        try {
          const data = await buscarTickets({
              searchParam,
              filtroContato,
              buscaMensagem,
              escopoBuscaMensagem,
              pageNumber,
              status,
              date,
              // Recorte por período. Vai só quando a aba pede — hoje a de
              // encerrados, que sem isso cresce para sempre.
              ultimosDias,
              showAll,
              queueIds,
              semFila,
              withUnreadMessages,
              isGroup,
              // Eixo dono/setor da aba ("minhas" | "setor"); sem ele a
              // lista continua recortada só pela situação.
              visao,
              onlyFavorites: onlyFavorites ? "true" : undefined,
              soAdiados: soAdiados ? "true" : undefined,
              // O backend espera JSON de ids; array cru viraria
              // "1,2" e ele nao saberia separar.
              tagIds: tagIds?.length ? JSON.stringify(tagIds) : undefined,
              userIds: userIds?.length ? JSON.stringify(userIds) : undefined,
              whatsappIds: whatsappIds?.length
                ? JSON.stringify(whatsappIds)
                : undefined,
              // Motivo do encerramento: chaves, não ids ("sem_retorno").
              closeReasons: closeReasons?.length
                ? JSON.stringify(closeReasons)
                : undefined,
              needsCount,
              ordem,
          });
          if (!atual) return;

          setTickets(data.tickets);

          // O encerramento automático por inatividade saiu daqui: rodava
          // no navegador, então só acontecia enquanto alguém tivesse a tela
          // aberta, e o mesmo prazo valia para todas as conexões. Agora é
          // regra da conexão, aplicada pelo worker (ApplyTicketRulesService).

          setHasMore(data.hasMore);
          setCount(data.count);
          if (data.contagemPorSituacao) {
            setContagemPorSituacao(data.contagemPorSituacao);
          }
          setErro(false);
          setLoading(false);
        } catch (err) {
          if (!atual) return;

          setLoading(false);
          setErro(true);
          toastError(err);
        }
      };

      fetchTickets();
      // Meio segundo existe para não disparar uma busca por tecla digitada.
      // Fora da busca não há nada a esperar: abrir a tela, trocar de aba ou
      // pedir a página seguinte pagava esse atraso à toa, e era ele que a
      // pessoa via como "a lista demora a aparecer".
    }, searchParam || filtroContato || buscaMensagem ? 500 : 0);
    return () => {
      atual = false;
      clearTimeout(delayDebounceFn);
    };
  }, [
    searchParam,
    filtroContato,
    buscaMensagem,
    escopoBuscaMensagem,
    pageNumber,
    status,
    date,
    showAll,
    queueIds,
    semFila,
    withUnreadMessages,
    isGroup,
    tagIds,
    userIds,
    whatsappIds,
    visao,
    onlyFavorites,
    soAdiados,
    ultimosDias,
    closeReasons,
    needsCount,
    ordem,
    tentativa,
  ]);

  return {
    tickets,
    loading,
    hasMore,
    count,
    erro,
    tentarDeNovo,
    contagemPorSituacao,
  };
};

export default useTickets;
